/**
 * Logic dùng chung của các khối (không phải component): ngữ cảnh hiển thị, màu/ký hiệu thanh,
 * phát audio, điểm đường cao độ. Component dùng chung nằm ở `ui.tsx`.
 */
import { createContext, useContext } from "react";

import type { Curve } from "./types";

// ─── Ngữ cảnh hiển thị (thay cho Furigana/Romaji của khoá N5) ─────────────

export interface ViewOptions {
  /** Hiện phiên âm (pinyin). */
  py: boolean;
  /** Hiện âm Hán Việt. */
  hv: boolean;
  /** Hiện chữ phồn thể thay giản thể. */
  phon: boolean;
}

export const ViewContext = createContext<ViewOptions>({ py: true, hv: true, phon: false });
export const useView = () => useContext(ViewContext);

// ─── Font + màu thanh điệu ───────────────────────────────────────────────

export const HAN_FONT =
  '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", "Hiragino Sans GB", sans-serif';
export const HAN_SERIF = '"Noto Serif SC", "Songti SC", "SimSun", serif';

export const TONE_COLORS: Record<number, string> = {
  0: "#8c8c8c",
  1: "#cf1322",
  2: "#d46b08",
  3: "#389e0d",
  4: "#0958d9",
};
export const TONE_MARKS: Record<number, string> = { 1: "ˉ", 2: "ˊ", 3: "ˇ", 4: "ˋ" };
export const TONE_NAMES: Record<number, string> = {
  1: "Thanh 1",
  2: "Thanh 2",
  3: "Thanh 3",
  4: "Thanh 4",
};
/** Màu theo thanh đọc thực tế; 21 (nửa thanh 3) tô như thanh 3. */
export const toneColor = (t: number | undefined) =>
  TONE_COLORS[t === 21 ? 3 : (t ?? 0)] ?? TONE_COLORS[0];

export const chuOf = (w: { chu: string; phon?: string }, phon: boolean) =>
  phon && w.phon ? w.phon : w.chu;

// ─── Phát audio: chỉ 1 nguồn phát tại một thời điểm ──────────────────────

let current: HTMLAudioElement | null = null;

/** Phát audio, resolve `true` khi phát xong (`false` nếu lỗi/không có nguồn). */
export function playAudio(src: string | undefined): Promise<boolean> {
  return new Promise((resolve) => {
    if (!src) return resolve(false);
    if (current) current.pause();
    const audio = new Audio(src);
    current = audio;
    audio.onended = () => resolve(true);
    audio.onerror = () => resolve(false);
    audio.play().catch(() => resolve(false));
  });
}

export function stopAudio(): void {
  if (current) current.pause();
}

// ─── Đường cao độ mẫu → điểm vẽ ──────────────────────────────────────────

/** Đường mẫu → điểm (x 0..1, y semitone), bỏ khung không có thanh. */
export function curvePoints(c: Curve | null | undefined): [number, number][] {
  if (!c) return [];
  const pts: [number, number][] = [];
  c.st.forEach((v, i) => {
    if (v != null) pts.push([(i * c.hop) / c.dur, v]);
  });
  if (!pts.length) return pts;
  const a = pts[0][0];
  const b = pts[pts.length - 1][0];
  return pts.map(([x, y]) => [(x - a) / (b - a || 1), y]);
}
